<template>
  <SubNavigation
    :title="pageData.name"
    :children-links="pageData.parentNodePage?.children"
    bg-color="bg-[#E4E4E4]"
  />
  <Selector
    :placeholder="t('publications.all_publications')"
    v-model="selectedOption"
    :options="categoryOptions"
    bg-color="bg-[#E4E4E4]"
    class="bp1680:w-[506px] mx-5 mx-auto mt-10 mb-25 w-[275px]! sm:w-[366px]! sm:w-[calc(100%-40px)] md:mx-auto md:w-[354px]! lg:w-[452px]! xl:mb-30! xl:w-[426px]! 2xl:mb-37.5! 2xl:w-[506px]!"
  />

  <Transition
    enter-active-class="transition-all duration-600 ease-out overflow-hidden"
    enter-from-class="opacity-0 -translate-y-4 max-h-0"
    enter-to-class="opacity-100 translate-y-0 max-h-[500px]"
    leave-active-class="transition-all duration-300 ease-in overflow-hidden"
    leave-from-class="opacity-100 translate-y-0 max-h-[500px]"
    leave-to-class="opacity-0 -translate-y-4 max-h-0"
  >
    <Container
      v-if="
        featuredPublications?.length > 0 &&
        (selectedOption.slug === 'vse' || selectedOption.slug === 'all')
      "
    >
      <PublicationSlider
        :slides="featuredPublications"
        :slider-title="t('publications.new_titles')"
      />
    </Container>
  </Transition>

  <Container class="mt-25 mb-30 xl:mt-37.5 xl:mb-42.5">
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-3"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-3"
    >
      <div
        :key="selectedOption?.slug"
        class="mx-auto grid w-75 grid-cols-1 gap-y-10 md:w-162.5 md:grid-cols-2 md:gap-x-12.5 md:gap-y-37.5 lg:w-167.5 2lg:w-260! 2lg:grid-cols-3! 2lg:gap-x-17.5!"
      >
        <PublicationCard
          v-for="publication in publicationItems"
          :key="publication.id"
          :publication="publication"
          eager
        />
      </div>
    </Transition>

    <ShowMoreButton
      v-if="hasMorePages"
      :is-expanded="false"
      type="border"
      class="mt-15 xl:mt-20"
      button-color="dark"
      @click="loadMore"
    />
  </Container>
</template>

<script setup lang="ts">
import type { Publication, Category, Tag, PageData } from '~/types'

const props = defineProps<{ pageData: PageData }>()

const { locale, t } = useI18n()
const route = useRoute()
const router = useRouter()

const PER_PAGE = 21

const page = ref(Number(route.query.page) || 1)
const fetchFirst = computed(() => PER_PAGE * page.value)

useDynamicSeo(props.pageData, {
  bodyAttrs: {
    style: 'background-color: #E4E4E4',
  },
})

const initialCategory = (route.query.category as string) || ''
const initialFilterSlug =
  initialCategory && initialCategory !== 'vse' && initialCategory !== 'all'
    ? initialCategory
    : undefined

const tagSlugsRef = ref<string[] | undefined>(
  initialFilterSlug ? [initialFilterSlug] : undefined,
)

const publicationVariables = reactive({
  first: fetchFirst,
  page: 1,
  tagSlugs: computed(() => tagSlugsRef.value),
})

const [
  { data: categoryData },
  { data: publicationTags },
  { data: publicationData },
] = await Promise.all([
  usePersistedFetch('GetPublicationCategoriesPaginated', { first: 1 }),
  usePersistedFetch('PublicationTags'),
  usePersistedFetch('GetPublicationPaginated', publicationVariables, {
    watch: [fetchFirst, tagSlugsRef],
  }),
])

const featuredPublications = computed(
  () =>
    categoryData.value?.getPublicationCategoriesPaginated?.data?.at(0)
      ?.featuredPublications,
)

const categoryOptions = computed(() => {
  return [
    {
      name: t('publications.all_publications'),
      slug: locale.value === 'cs' ? 'vse' : 'all',
    },
    ...(publicationTags.value?.getPublicationTags?.map((t: any) => ({
      name: t.name,
      slug: t.slug[locale.value],
    })) || []),
  ]
})

const selectedOption = ref<Category>(
  categoryOptions.value?.find(
    (opt: Category) => opt.slug === initialCategory,
  ) ?? categoryOptions.value[0]!,
)

const publicationItems = ref<Publication[]>([])
const hasMorePages = ref(false)

watch(
  () => publicationData.value,
  (newVal) => {
    const paginated = newVal?.getPublicationPaginated
    publicationItems.value = paginated?.data || []
    hasMorePages.value = paginated?.paginatorInfo?.hasMorePages || false
  },
  { immediate: true },
)

watch(selectedOption, (newVal: Category) => {
  page.value = 1
  const slug = newVal?.slug
  tagSlugsRef.value =
    !slug || slug === 'vse' || slug === 'all' ? undefined : [slug]

  router.push({
    query: {
      ...route.query,
      page: undefined,
      category: slug !== 'vse' && slug !== 'all' ? slug : undefined,
    },
  })
})

watch(
  () => route.query,
  (newQuery) => {
    const qPage = Number(newQuery.page) || 1
    if (qPage !== page.value) {
      page.value = qPage
    }

    const categoryOpt =
      categoryOptions.value.find((opt) => opt.slug === newQuery.category) ||
      categoryOptions.value[0]

    if (selectedOption.value?.slug !== categoryOpt?.slug) {
      selectedOption.value = categoryOpt
    }
  },
  { deep: true },
)

const loadMore = () => {
  page.value++

  router.push({
    query: {
      ...route.query,
      page: page.value > 1 ? String(page.value) : undefined,
    },
  })
}
</script>
